import React, { useEffect, useState } from 'react';
import { CocoEvent } from '@cotera/client/app/etc';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import type { QueryClient } from '@tanstack/react-query';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading, toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { CocoChatModel } from '../../chat/models/coco-chat.model';
import {
  ResourceSelectionModel,
  ResourceType,
} from '../selection/resource-selection.model';

const renameChatParamsSchema = z.object({
  title: z.string().min(1),
});

type Params = z.infer<typeof renameChatParamsSchema>;

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

/** Only the client surface this action actually calls. */
type RenameChatActionClient = {
  resource: {
    chat: Pick<TenantedClient['resource']['chat'], 'rename'>;
  };
};

/** The chat this action targets, plus its current title (for the ask view). */
type RenameTarget = {
  conversationId: string;
  initialName: string;
};

export class RenameChatAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Rename';
  description = 'Rename this chat';
  inputSchema = renameChatParamsSchema;
  group = 'app';
  icon = 'pencil';

  private _lastRenamedConversationId: string | null = null;

  constructor(
    private readonly client: RenameChatActionClient,
    private readonly queryClient: Pick<QueryClient, 'invalidateQueries'>,
    private readonly orgId: string,
    private readonly modalManager: ModalManager
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTarget(context) !== null;
  }

  getPriority(context: ApplicableContext): number {
    return this.getTarget(context) !== null ? 20 : 0;
  }

  /**
   * Ask only when the caller has no name to give.
   *
   * The chat header's title is click-to-edit, so it arrives with the name
   * already collected and a modal in front of it would be the same question
   * twice. Callers that cannot collect one — a list row's menu, any generic
   * action surface — still call `execute({})` and still get the dialog.
   */
  shouldAsk(_context: ApplicableContext, payload: Partial<Params>): boolean {
    return payload.title === undefined;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const target = this.getTarget(context);
    if (target === null) {
      return err([{ t: 'error', message: 'No chat in scope' }]);
    }

    const title = payload.title.trim();
    if (title.length === 0) {
      return err([{ t: 'error', message: 'Title cannot be empty' }]);
    }

    const result = await this.client.resource.chat.rename({
      conversationId: target.conversationId,
      title,
    });

    if (result.isErr()) {
      const msg =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to rename chat';
      toast.error(msg);
      return err([{ t: 'error', message: msg }]);
    }

    // Reflect the new title immediately on the open chat page; the server
    // ResourceUpdated event refreshes lists/other clients.
    const chatModel = context.targetedModelOfType(CocoChatModel);
    chatModel?.setOptimisticTitle(title);

    void this.queryClient.invalidateQueries({
      queryKey: queryKeys.chat.conversations({ orgId: this.orgId }),
    });
    void this.queryClient.invalidateQueries({
      queryKey: queryKeys.resource.list({
        orgId: this.orgId,
        type: ResourceType.Chat,
        limit: 50,
      }),
    });

    const selection = context.targetedModelOfType(ResourceSelectionModel);
    if (selection !== null) {
      context.markAsNotTargeted(selection);
    }

    this._lastRenamedConversationId = target.conversationId;
    toast.success('Chat renamed');
    return ok({});
  }

  track(): ActionTrackMetadata | null {
    if (this._lastRenamedConversationId === null) {
      return null;
    }
    return {
      event: CocoEvent.ChatRenamed,
      properties: { conversationId: this._lastRenamedConversationId },
    };
  }

  ask(
    params: Partial<Params>,
    context: ExecuteContext
  ): AskResponse<Params, { t: string; message?: string }[]> {
    const target = this.getTarget(context);
    const initialName = params.title ?? target?.initialName ?? '';
    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async () => {
          await onCancel();
          this.modalManager.pop();
        };

        this.modalManager.push({
          view: () => (
            <RenameChatAskView
              initialName={initialName}
              onRespond={async (nextParams) => {
                await onRespond(nextParams);
                this.modalManager.pop();
              }}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }

  private getTarget(context: ApplicableContext): RenameTarget | null {
    // A single selected chat row (list views / ellipsis menu on a row) wins.
    if (context.isInScope(ResourceSelectionModel)) {
      const selection = context.getInScopeModelsOfType(ResourceSelectionModel);
      if (selection.length === 1 && selection[0]?.selections.length === 1) {
        const chat = selection[0]?.getResourceForType(ResourceType.Chat);
        if (chat !== undefined) {
          return { conversationId: chat.resourceId, initialName: chat.name };
        }
      }
    }

    const chatModel = context.targetedModelOfType(CocoChatModel);
    if (chatModel !== null && chatModel.conversationId !== '') {
      return {
        conversationId: chatModel.conversationId,
        initialName: currentChatTitle(chatModel),
      };
    }

    return null;
  }
}

/** Best-effort current title for the ask view's initial value. */
function currentChatTitle(chatModel: CocoChatModel): string {
  const optimistic = chatModel.optimisticTitleWatchable.snapshot();
  if (optimistic !== null && optimistic.trim() !== '') {
    return optimistic.trim();
  }
  const serverTitle = chatModel.conversationWatchable.snapshot()?.title();
  if (serverTitle !== undefined && serverTitle.trim() !== '') {
    return serverTitle.trim();
  }
  const seeded = chatModel.seededDisplayTitleWatchable.snapshot();
  if (seeded !== null && seeded.trim() !== '') {
    return seeded.trim();
  }
  return '';
}

const RenameChatAskView: React.FC<{
  initialName: string;
  onRespond: (params: Params) => Promise<void>;
  onCancel: () => Promise<void>;
}> = ({ initialName, onRespond, onCancel }) => {
  const [name, setName] = useState(initialName);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setName(initialName);
  }, [initialName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0 || loading) {
      return;
    }
    setLoading(true);
    await onRespond({ title: trimmed });
    setLoading(false);
  };

  return (
    <ModalPageView
      title="Rename chat"
      subtitle="Choose a new name for this chat"
      className="px-4 py-4"
    >
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-2">
          <label htmlFor="chat-name" className="text-sm font-medium">
            Chat name
          </label>
          <Input
            id="chat-name"
            autoFocus
            placeholder="Chat name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void onCancel()}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading || name.trim().length === 0}>
            {loading ? <Loading.Spinner variant="sm" /> : 'Rename'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
